// @flow

import * as React from 'react';
import {useState, useEffect} from 'react';
import {defineMessages, useIntl, FormattedMessage} from 'react-intl';
import {useAccount, useApi} from 'docs/data';
import {
  CHECKOUT_CURRENCIES,
  SUBUNIT_TO_UNITS,
  useMerchantCurrency,
  useFormatPriceAmount,
} from 'docs/hooks';
import {isBrowser} from 'docs/util';
import Box from 'sail/Box';
import {AlignBox} from 'sail/Align';
import Group from 'sail/Group';
import {BodyAlt, HeadingTextProps} from 'sail/Text';
import TruncatedText from 'sail/TruncatedText';
import Icon from 'sail/Icon';
import {Button, SubmitButton, ButtonLink} from 'sail/Button';
import FormLayout, {FormRow, FormField} from 'sail/FormLayout';
import {TextInput, InputGroup} from 'sail/Input';
import Select, {SelectOption} from 'sail/Select';
import {SearchableSelect} from 'sail/Search';
import {InfoTooltip} from 'sail/Tooltip';
import type {SearchableSelectResultProps} from 'sail/Search';

import {useData} from 'docs/data/integration-builder';
import type {DataContextType} from 'docs/data/integration-builder';
import type {IconType} from 'sail/Icon/types';

import './ProductPicker.scss';

const messages = defineMessages({
  error: {
    id: 'docsShell.ib.orders.html.line-items.option.error',
    defaultMessage: 'An unexpected error occurred. Please try again.',
  },
  chooseLabel: {
    id: 'docsShell.ib.orders.html.line-items.option.chooseLabel',
    defaultMessage: 'Select a test product or create a new one',
  },
  // TODO(areagle): add tax_code option
  addProduct: {
    id: 'docsShell.ib.orders.html.line-items.option.addProduct',
    defaultMessage: 'Add new test product',
  },
  createProduct: {
    id: 'docsShell.ib.orders.html.line-items.option.createProduct',
    defaultMessage: 'Create test product',
  },
  createProductTooltip: {
    id: 'docsShell.ib.orders.html.line-items.option.createProductTooltip',
    defaultMessage:
      'When you’re ready to go live, remember to update this with a live mode product.',
  },
  learnMore: {
    id: 'docsShell.ib.orders.html.line-items.option.learnMore',
    defaultMessage: 'Learn more',
  },
  cancel: {
    id: 'docsShell.ib.orders.html.line-items.option.cancel',
    defaultMessage: 'Cancel',
  },
  moreProductOptions: {
    id: 'docsShell.ib.orders.html.line-items.option.moreProductOptions',
    defaultMessage: 'More product options',
  },
  name: {
    id: 'docsShell.ib.orders.html.line-items.option.name',
    defaultMessage: 'Name',
  },
  namePlaceholder: {
    id: 'docsShell.ib.orders.html.line-items.option.namePlaceholder',
    defaultMessage: 'Sunglasses, premium plan, etc.',
  },
  price: {
    id: 'docsShell.ib.orders.html.line-items.option.price',
    defaultMessage: 'Price',
  },
});

const INPUT_WIDTH = 236;

const searchProps = {
  onChangeQuery: () => {},
  queryString: '',
  status: 'completed',
};

const ADD = '__ADD__';

type ItemProps = {|
  highlighted: boolean,
  label: string,
  icon: ?IconType,
|};

const Item = ({highlighted, label, icon}: ItemProps) => {
  return (
    <Box className="ProductPicker--item">
      <Box
        className="ProductPicker--item-container"
        background={highlighted ? 'blue' : 'white'}
        padding={{horizontal: 8, vertical: 2}}
      >
        <AlignBox reference={HeadingTextProps} style={{alignItems: 'center'}}>
          <Group direction="horizontal" spacing={8}>
            {icon ? (
              <Icon
                icon={icon}
                color={highlighted ? 'white' : undefined}
                size={12}
              />
            ) : null}
            <BodyAlt color={highlighted ? 'white' : undefined}>
              <TruncatedText>{label}</TruncatedText>
            </BodyAlt>
          </Group>
        </AlignBox>
      </Box>
    </Box>
  );
};

type ProductPickerComponentProps = {|
  products: $PropertyType<DataContextType, 'products'>,
|};

const ProductPickerComponent = ({products}: ProductPickerComponentProps) => {
  const intl = useIntl();
  const {account} = useAccount();
  const {api} = useApi();
  const defaultCurrency = useMerchantCurrency();
  const {
    activeProduct: selectedProduct,
    setActiveProduct: setSelectedProduct,
    setProducts,
  } = useData();
  const formatPriceAmount = useFormatPriceAmount();

  const [showPicker, setShowPicker] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [name, setName] = useState('');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState(
    defaultCurrency ? defaultCurrency.toUpperCase() : 'USD',
  );

  const getButtonLabel = (entry) => {
    if (entry.id === ADD) {
      return intl.formatMessage(messages.addProduct);
    }
    return entry.default_price
      ? `${entry.name} — ${formatPriceAmount(entry.default_price)}`
      : `${entry.name}`;
  };

  useEffect(() => {
    /**
     * Shift to show the product picker if products are loaded.
     * This will also fire after a product has been created,
     * shifting back to the product picker.
     */
    if (products.length) {
      setShowPicker(true);
    }
  }, [products]);

  if (!account.merchant?.isLoggedIn) {
    return null;
  }

  if (showPicker) {
    return (
      <Group
        className="ProductPicker"
        margin={{top: 8, bottom: 16}}
        direction="horizontal"
        alignItems="center"
        spacing={8}
      >
        <SearchableSelect
          {...searchProps}
          results={[{id: ADD}, ...products]}
          autoflip
          chooseLabel={intl.formatMessage(messages.chooseLabel)}
          onChange={(product) => {
            if (product?.id === ADD) {
              return setShowPicker(false);
            }
            setSelectedProduct(product);
          }}
          value={selectedProduct}
          width="long"
          renderInput={() => null}
          renderItem={(props: SearchableSelectResultProps<Object>) => {
            return (
              <Item
                highlighted={props.highlighted}
                label={getButtonLabel(props.entry)}
                icon={props.entry.id === ADD ? 'add' : undefined}
              />
            );
          }}
          getButtonLabel={getButtonLabel}
          getFormValue={(entry) => entry.id}
        />
        <InfoTooltip
          allowMouseOverContents
          content={
            <>
              <FormattedMessage {...messages.createProductTooltip} />{' '}
              <ButtonLink
                display="inline-flex"
                label={<FormattedMessage {...messages.learnMore} />}
                icon="external"
                iconPosition="right"
                target="_blank"
                href="/docs/products-prices/manage-prices"
              />
            </>
          }
        />
      </Group>
    );
  }

  async function onSubmit(e) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const subUnitToUnit = SUBUNIT_TO_UNITS[currency.toUpperCase()] || 100;
      const response = await api.createProductAndPrice({
        name,
        unit_amount: Math.floor(Number(amount) * subUnitToUnit),
        currency,
        set_default_price: true,
      });
      if (response) {
        if (response.error) {
          setError(response.error);
        } else if (response.id) {
          setSelectedProduct(response.product);
          setProducts((current) => [response.product, ...current]);

          setName('');
          setAmount('');
        }
      }
    } catch {
      setError(intl.formatMessage(messages.error));
    } finally {
      setLoading(false);
    }
  }

  const {maximumFractionDigits, minimumFractionDigits} = typeof isBrowser
    ? new Intl.NumberFormat(account.locale?.code, {
        style: 'currency',
        currency,
      }).resolvedOptions()
    : {maximumFractionDigits: 2, minimumFractionDigits: 2};
  return (
    <Box
      className="ProductPicker--form"
      flex={{direction: 'column'}}
      margin={{bottom: 16}}
    >
      <form onSubmit={onSubmit}>
        <FormLayout
          layout="stacked"
          divider={false}
          size="small"
          style={{background: 'transparent'}}
        >
          <FormRow
            label={intl.formatMessage(messages.name)}
            style={{padding: '20px 0 0'}}
          >
            <FormField>
              <TextInput
                required
                aria-label={intl.formatMessage(messages.name)}
                placeholder={intl.formatMessage(messages.namePlaceholder)}
                value={name}
                onChange={setName}
                width={INPUT_WIDTH}
                maxLength={250}
              />
            </FormField>
          </FormRow>
          <FormRow
            label={intl.formatMessage(messages.price)}
            style={{padding: '20px 0 0'}}
          >
            <FormField>
              <InputGroup direction="horizontal" invalid={false}>
                <TextInput
                  required
                  start={{
                    'aria-label': currency,
                    content: intl
                      .formatNumber(0, {
                        style: 'currency',
                        currency,
                        maximumFractionDigits: 0,
                      })
                      .replace('0', ''),
                  }}
                  placeholder={intl.formatNumber(0.0, {
                    style: 'decimal',
                    minimumFractionDigits,
                    maximumFractionDigits,
                  })}
                  type="number"
                  value={amount}
                  min={0}
                  max={999999999999}
                  step=".01"
                  onChange={(newAmount) => {
                    const match = newAmount.match(/^-?\d+(?:[.,]\d{0,2})?/);

                    setAmount(match ? match[0] : newAmount);
                  }}
                />
                <Select
                  required
                  defaultValue="usd"
                  value={currency}
                  onChange={setCurrency}
                >
                  {CHECKOUT_CURRENCIES.map((c) => (
                    <SelectOption key={c} value={c} label={c} />
                  ))}
                </Select>
              </InputGroup>
            </FormField>
          </FormRow>
          <FormRow style={{padding: '28px 0 0'}}>
            <FormField error={error}>
              <Group
                spacing={8}
                direction="horizontal"
                justifyContent="space-between"
                alignItems="center"
              >
                <Group spacing={12} direction="horizontal" alignItems="center">
                  <SubmitButton
                    label={<FormattedMessage {...messages.createProduct} />}
                    icon="add"
                    pending={loading}
                  />
                  {products.length ? (
                    <Button
                      label={<FormattedMessage {...messages.cancel} />}
                      disabled={loading}
                      onClick={() => setShowPicker(true)}
                    />
                  ) : null}
                </Group>
                <ButtonLink
                  label={<FormattedMessage {...messages.moreProductOptions} />}
                  icon="external"
                  iconPosition="right"
                  target="_blank"
                  href="https://dashboard.stripe.com/test/products/create"
                />
              </Group>
            </FormField>
          </FormRow>
        </FormLayout>
      </form>
    </Box>
  );
};

export const ProductPicker = () => {
  const {products} = useData();
  return <ProductPickerComponent products={products} />;
};
